import type { FunctionComponent } from 'react';

import { hardRedirectTo } from '../../../utils';
import AuthLoginMethodLogoBitBucket from '../../auth/form/LoginMethodLogos/BitBucket';
import AuthLoginMethodLogoGitHub from '../../auth/form/LoginMethodLogos/GitHub';
import AuthLoginMethodLogoGitLab from '../../auth/form/LoginMethodLogos/GitLab';
import AuthLoginMethodLogoGoogle from '../../auth/form/LoginMethodLogos/Google';
import Button from '../../generic/Button/Button';
import { socialLoginEntryUrl } from '../ssoEntry';
import { STRATEGY_LABEL } from '../strategyLabels';
import type { OAuthProvider } from '../../../../shared/types/routes/auth/v2';

import styles from './SocialLoginButtons.module.less';

// A no-prop function component (not `ComponentType`): its class-component branch resolves `render()` against
// react-bootstrap's bundled `@types/react`, which trips TS2786 under the strict project config.
// Keyed by the wire `OAuthProvider` literal so this consumes `GET /api/auth/state`'s `oauthProviders` directly — no
// adapter. Entry URLs come from the shared `socialLoginEntryUrl` (the V2 route); display labels from `STRATEGY_LABEL`.
const PROVIDER_LOGO: Record<OAuthProvider, FunctionComponent> = {
  google: AuthLoginMethodLogoGoogle,
  github: AuthLoginMethodLogoGitHub,
  gitlab: AuthLoginMethodLogoGitLab,
  bitbucket_cloud: AuthLoginMethodLogoBitBucket,
};

// Stable display order, independent of the order providers arrive in.
const PROVIDER_ORDER: OAuthProvider[] = ['google', 'github', 'gitlab', 'bitbucket_cloud'];

type SocialVariant = 'login' | 'signup';

const DIVIDER_TEXT: Record<SocialVariant, string> = {
  login: 'or sign in with',
  signup: 'or sign up with',
};

const ACTION_TEXT: Record<SocialVariant, string> = {
  login: 'Sign in',
  signup: 'Sign up',
};

export type SocialLoginButtonsProps = {
  providers: OAuthProvider[];
  /** Post-login redirect from a `?next=` deep link. Threaded onto the (V2) entry so it survives the round-trip. */
  next?: string;
  /** Switches the copy for the login vs the register page; the shared OAuth entry itself is identical. */
  variant?: SocialVariant;
  /** On the register page, threaded onto the OAuth entry so an invited social signup auto-joins the inviting org. */
  inviteToken?: string;
};

/**
 * The "or sign in/up with" social-login buttons, driven directly by the wire `OAuthProvider[]`. Each button navigates
 * to its provider's entry point.
 */
const SocialLoginButtons = ({ providers, next, variant = 'login', inviteToken }: SocialLoginButtonsProps) => {
  const enabledProviders = PROVIDER_ORDER.filter(provider => providers.includes(provider));

  if (enabledProviders.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.divider}>{DIVIDER_TEXT[variant]}</div>
      <ul className={styles.methods}>
        {enabledProviders.map(provider => {
          const Logo = PROVIDER_LOGO[provider];
          const label = STRATEGY_LABEL[provider];
          return (
            <li key={provider} className={styles.method}>
              <Button
                type="secondary"
                size="large"
                title={`${ACTION_TEXT[variant]} using ${label}`}
                aria-label={`${ACTION_TEXT[variant]} using ${label}`}
                onClick={() => hardRedirectTo(socialLoginEntryUrl(provider, next, inviteToken))}
              >
                <Logo />
              </Button>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default SocialLoginButtons;
